import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { JobRun } from "~/components/job-log";
import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "~/components/ui";
import type { DipPipelineDryRunResultTarget, DipPipelineJobExecution } from "~/features/dataImportProjects/types";
import { BatchChangeRequestsJobDryRunResultDialog } from "./BatchChangeRequestsJobDryRunResultDialog";
import { DryRunResultDialog as BatchEvidencesJobDryRunResultDialog } from "./BatchEvidencesJobDryRunResultDialog";

// A human-friendly label for a job, resolved from the project's job definitions.
// `name` is the job name, shown (truncated) instead of the raw jobId.
export type JobRunLabel = {
  name: string;
};

type PipelineDryRunResultDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: DipPipelineDryRunResultTarget | null;
  // jobId (batch evidences / change requests job definition id) -> friendly label.
  jobLabels: Map<string, JobRunLabel>;
};

function ResultRunList({
  title,
  runs,
  jobLabels,
  onOpen,
}: {
  title: string;
  runs: DipPipelineJobExecution[];
  jobLabels: Map<string, JobRunLabel>;
  onOpen: (run: DipPipelineJobExecution) => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="space-y-2">
      <h3 className="text-14-bold text-fg-1">{title}</h3>
      {runs.length > 0 ? (
        <div className="divide-y divide-border-default rounded-[var(--radius-md)] border border-border-default">
          {runs.map((run) => {
            const label = jobLabels.get(run.jobId);
            return (
              <div key={run.jobRunId} className="flex items-center gap-3 px-3 py-2">
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="truncate text-12-bold text-fg-1" title={label?.name ?? run.jobId}>
                    {label?.name ?? run.jobId}
                  </p>
                  <p className="truncate font-mono text-12-regular text-fg-3">
                    {t("model.common.id")}: {run.jobRunId}
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => onOpen(run)}>
                  {t("ui.openX", { x: t("ui.result") })}
                </Button>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="rounded-[var(--radius-md)] border border-border-default px-3 py-2 text-12-regular text-fg-3">
          {t("feature.common.noData")}
        </p>
      )}
    </section>
  );
}

export function PipelineDryRunResultDialog({ open, onOpenChange, target, jobLabels }: PipelineDryRunResultDialogProps) {
  const { t } = useTranslation();
  const [batchEvidencesRun, setBatchEvidencesRun] = useState<DipPipelineJobExecution | null>(null);
  const [batchChangeRequestsRun, setBatchChangeRequestsRun] = useState<DipPipelineJobExecution | null>(null);
  const batchEvidencesJobRun: JobRun | null = batchEvidencesRun
    ? { jobRunId: batchEvidencesRun.jobRunId, jobName: "BatchEvidencesJob", dryRun: true }
    : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("feature.dataImportProjects.v2.run.title")}</DialogTitle>
            {target ? (
              <DialogDescription>
                {t("ui.dryRunResult")} / {t("model.common.id")}: {target.jobRunId}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          {target ? (
            <div className="grid gap-5">
              <ResultRunList
                title={t("model.batchEvidencesJob.modelName")}
                runs={target.batchEvidencesJobRuns}
                jobLabels={jobLabels}
                onOpen={setBatchEvidencesRun}
              />
              <ResultRunList
                title={t("model.batchChangeRequestsJob.modelName")}
                runs={target.batchChangeRequestsJobRuns}
                jobLabels={jobLabels}
                onOpen={setBatchChangeRequestsRun}
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <BatchEvidencesJobDryRunResultDialog
        open={batchEvidencesRun !== null}
        onOpenChange={(nextOpen) => !nextOpen && setBatchEvidencesRun(null)}
        jobRun={batchEvidencesJobRun}
      />
      <BatchChangeRequestsJobDryRunResultDialog
        open={batchChangeRequestsRun !== null}
        onOpenChange={(nextOpen) => !nextOpen && setBatchChangeRequestsRun(null)}
        jobRunId={batchChangeRequestsRun?.jobRunId ?? null}
        jobId={batchChangeRequestsRun?.jobId ?? ""}
      />
    </>
  );
}
